import { FC, useCallback, useState, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import {
    FaFile,
    FaPowerOff,
    FaUser,
    FaBars,
    FaTimes,
    FaChartBar,
    FaBalanceScale,
    FaQuestion,
    FaExpand
} from 'react-icons/fa';

import cn from 'classnames';

import DashboardButton from '@components/DashboardButton';
import Dropdown from '@components/Dropdown';
import AboutModal from '@components/AboutModal';
import ProfileModal from '@components/ProfileModal';
import CapabilityModal from '@components/CapabilityModal';

import { getLoginProvider, logout } from '@helpers/utils';
import { LoginProviders } from '@helpers/enums';

import useViewportChange from '@helpers/useViewportChange';

import { loginRequest } from '@providers/azureADAuthConfig';

// @ts-ignore
import methodologyFile from '@assets/ws_glri_2024.pdf';
 // @ts-ignore
import methodologyFileTool from '@assets/230116_WS_Labour_Navigator_Methodology_release_1.4.pdf';
// @ts-ignore
import manualFile from '@assets/230116-Whiteshield_Labour_Navigator_Manual_release_1.4.pdf';

import './NavBar.scss';

const dropdownItems = [
    { id: 'profile', title: 'My Profile', icon: FaUser },
    { id: 'logout', title: 'Log Out', icon: FaPowerOff },
];

const title = process.env.REACT_APP_TITLE || '';

const methodologydropdownItems = [
    {
        id: 'file',
        title: 'Labour Resilience',
        icon: FaFile,
    },
    { id: 'methodology', title: title, icon: FaFile },
];


const aboutdropdownItems = [
    {
        id: 'about',
        title: title,
        icon: FaExpand,
    },
    { id: 'manual', title: 'Manual', icon: FaQuestion },
];




type NavBarProps = {};

const NavBar: FC<NavBarProps> = () => {
    const { instance, accounts } = useMsal();

    const { isMobile } = useViewportChange();
    const [isOpenAboutModal, setIsOpenAboutModal] = useState<boolean>(false);
    const [isOpenCapabilityModal, setIsOpenCapabilityModal] =
        useState<boolean>(false);
    const [isOpenProfileModal, setIsOpenProfileModal] = useState<boolean>(false);
    const [isOpenNavbar, setIsOpenNavbar] = useState<boolean>(false);
    const navigate = useNavigate();

    const logoutUser = useCallback(() => {
        const loginProvider = getLoginProvider();
        switch (loginProvider) {
            case LoginProviders.Identity:
                logout();
                break;

            case LoginProviders.AzureAD:
                // If user already logged out from Microsoft, then msalInstance might return empty array of accounts
                if (accounts == null || accounts.length === 0) {
                    logout();
                }

                // If user already logged out from Microsoft, then msal instance might return error if we acquire a new token
                if (accounts && accounts.length > 0) {
                    instance
                        .acquireTokenSilent({
                            ...loginRequest,
                            account: accounts[0],
                        })
                        .then(() => {
                            // In all other cases try to redirect the user to logout Microsoft
                            instance.logout({
                                account: accounts[0],
                                postLogoutRedirectUri: "https://" + window.location.host + process.env.REACT_APP_LOGOUT_AZURE_AD_REDIRECT_URI
                            });
                        })
                        .catch(() => {
                            logout();
                        });
                }
                break;
        }
    }, [instance, accounts]);

    const handleDropdownItemClick = useCallback(({ id }) => {
        switch (id) {
            case 'file':
                const file_path = methodologyFile;
                const a = document.createElement('a');
                a.href = file_path;
                a.target = '_blank';
                a.download = file_path.slice(file_path.lastIndexOf('/') + 1);
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                break;


            case 'methodology':
                const file_path_methodology = methodologyFileTool;
                const a_methodology = document.createElement('a');
                a_methodology.href = file_path_methodology;
                a_methodology.target = '_blank';
                a_methodology.download = file_path_methodology.slice(file_path_methodology.lastIndexOf('/') + 1);
                document.body.appendChild(a_methodology);
                a_methodology.click();
                document.body.removeChild(a_methodology);
                break;


            case 'manual':
                const file_path_manual = manualFile;
                const a_manual = document.createElement('a');
                a_manual.href = file_path_manual;
                a_manual.target = '_blank';
                a_manual.download = file_path_manual.slice(file_path_manual.lastIndexOf('/') + 1);
                document.body.appendChild(a_manual);
                a_manual.click();
                document.body.removeChild(a_manual);
                break;


            case 'about':
                setIsOpenAboutModal(true);
                break;
                
            case 'logout':
                logoutUser();
                break;
            case 'capability':
                setIsOpenCapabilityModal(true);
                break;
            case 'profile':
                setIsOpenProfileModal(true);
                break;

            default:
                break;
        }
    }, [logoutUser]);

    //const handleAboutClick = useCallback(() => {
    //    setIsOpenAboutModal(true);
    //}, []);

    const handleCloseAboutModal = useCallback(() => {
        setIsOpenAboutModal(false);
    }, []);

    const handleCloseCapabilityModal = useCallback(() => {
        setIsOpenCapabilityModal(false);
    }, []);

    const handleOpenProfileModal = useCallback(() => {
        setIsOpenProfileModal(true);
    }, []);

    const handleCloseProfileModal = useCallback(() => {
        setIsOpenProfileModal(false);
    }, []);

    const toggleNavbar = useCallback(() => {
        setIsOpenNavbar(prevState => !prevState);
    }, []);

    const { topbarStyles, textAlignment } = useMemo<{
        textAlignment: 'center' | 'left';
        topbarStyles: { [key: string]: any };
    }>(() => {
        if (isMobile) {
            return {
                textAlignment: 'left',
                topbarStyles: {},
            };
        }

        return {
            textAlignment: 'center',
            topbarStyles: {
                marginLeft: 4,
            },
        };
    }, [isMobile]);

    const firstName = localStorage.getItem('firstName');

    return (
        <>
            {isMobile && (
                <div className="burger-menu" onClick={toggleNavbar}>
                    <FaBars />
                </div>
            )}
            <nav className={cn('topbar', { topbar_opened: isOpenNavbar })}>
                {isMobile && (
                    <div className="burger-menu__close-button" onClick={toggleNavbar}>
                        <FaTimes />
                    </div>
                )}
                {isMobile && (
                    <div className="burger-menu__user-name">{firstName !== 'null' ? firstName : ''}</div>
                )}
                {isMobile && (
                    <DashboardButton
                        label="My Profile"
                        theme="transparent"
                        icon={<FaUser />}
                        style={topbarStyles}
                        alignment={textAlignment}
                        onClick={handleOpenProfileModal}
                    />
                )}
                <DashboardButton
                    label="Dashboard"
                    theme="transparent"
                    icon={<FaChartBar />}
                    iconAlignment="start"
                    alignment={textAlignment}
                    style={topbarStyles}
                    onClick={() => navigate('/dashboard')}
                />
                <DashboardButton
                    label="Comparison Tool"
                    theme="transparent"
                    icon={<FaBalanceScale />}
                    iconAlignment="start"
                    alignment={textAlignment}
                    style={topbarStyles}
                    onClick={() => navigate('/compare')}
                />
                {/* {process.env.REACT_APP_LABOUR_FORECAST_ENABLED === "true" && 
                    <DashboardButton
                        label="Labour Forecast"
                        theme="transparent"
                        icon={<TrendingUpIcon />}
                        iconAlignment="start"
                        alignment={textAlignment}
                        style={topbarStyles}
                        onClick={() => navigate('/labourForecast')}
                    />
                } */}
                <Dropdown
                    hoverable
                    label="Methodology"
                    theme="transparent"
                    icon={<FaFile />}
                    items={methodologydropdownItems}
                    style={topbarStyles}
                    onSelect={handleDropdownItemClick}
                />
                <Dropdown
                    hoverable
                    label="About"
                    theme="transparent"
                    icon={<FaFile />}
                    items={aboutdropdownItems}
                    style={topbarStyles}
                    onSelect={handleDropdownItemClick}
                />


                {/*<DashboardButton*/}
                {/*    label="About"*/}
                {/*    theme="transparent"*/}
                {/*    style={topbarStyles}*/}
                {/*    alignment={textAlignment}*/}
                {/*    onClick={handleAboutClick}*/}
                {/*/>*/}


                {!isMobile && (
                    <Dropdown
                        hoverable
                        label={firstName !== 'null' ? firstName! : ''}
                        theme="transparent"
                        icon={<FaUser />}
                        items={dropdownItems}
                        style={topbarStyles}
                        onSelect={handleDropdownItemClick}
                    />
                )}
                {isMobile && (
                    <DashboardButton
                        label="Log Out"
                        theme="transparent"
                        icon={<FaPowerOff />}
                        style={topbarStyles}
                        alignment={textAlignment}
                        onClick={logoutUser}
                    />
                )}
            </nav>
            <AboutModal isOpen={isOpenAboutModal} onClose={handleCloseAboutModal} />
            <CapabilityModal
                isOpen={isOpenCapabilityModal}
                onClose={handleCloseCapabilityModal}
            />
            <ProfileModal
                isOpen={isOpenProfileModal}
                onClose={handleCloseProfileModal}
            />
        </>
    );
};

export default NavBar;
